// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';

import { useAuth } from '../../../context/AuthContext';
import type { LatLng } from '../../../types';
import { defaultCenterAPI, DefaultCenter } from '../../../utils/apiClient';
import { Button } from '../../common/Button';

interface Props {
  inline?: boolean;
  onClose: () => void;
}

// ── Map click handler (inside Leaflet context) ──────────────────────────────

interface PickerProps {
  onPick: (lat: number, lng: number) => void;
  picked: LatLng | null;
}

const MapClickHandler: React.FC<PickerProps> = ({ onPick, picked }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });

  if (!picked) return null;

  return <Marker position={picked} />;
};

// ── Main component ──────────────────────────────────────────────────────────

export const DefaultCenterSettings: React.FC<Props> = ({ inline, onClose }) => {
  const { setDefaultCenter: setAuthDefaultCenter } = useAuth();

  const [current, setCurrent] = useState<DefaultCenter>(null);
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const [zoomInput, setZoomInput] = useState('10');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedOnMap, setPickedOnMap] = useState<LatLng | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await defaultCenterAPI.get();
      if (!mountedRef.current) return;
      setCurrent(data.defaultCenter);
      if (data.defaultCenter) {
        setLatInput(String(data.defaultCenter.lat));
        setLngInput(String(data.defaultCenter.lng));
        setZoomInput(String(data.defaultCenter.zoom ?? 10));
      }
    } catch {
      if (mountedRef.current) setError('Could not load current default center.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handlePickOnMap = (lat: number, lng: number) => {
    const rounded = (v: number) => Math.round(v * 1e6) / 1e6;
    setPickedOnMap([lat, lng]);
    setLatInput(String(rounded(lat)));
    setLngInput(String(rounded(lng)));
  };

  const handleUsePickedLocation = () => {
    setPickerOpen(false);
  };

  const validate = (): { lat: number; lng: number; zoom: number } | null => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    const zoom = parseInt(zoomInput, 10);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      setError('Latitude must be a number between -90 and 90.');
      return null;
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      setError('Longitude must be a number between -180 and 180.');
      return null;
    }
    if (!Number.isInteger(zoom) || zoom < 1 || zoom > 20) {
      setError('Zoom must be a whole number between 1 and 20.');
      return null;
    }
    return { lat, lng, zoom };
  };

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const coords = validate();
    if (!coords) return;

    try {
      setSaving(true);
      const data = await defaultCenterAPI.set(coords.lat, coords.lng, coords.zoom);
      if (!mountedRef.current) return;
      setCurrent(data.defaultCenter);
      setAuthDefaultCenter(data.defaultCenter);
      setSaved(true);
    } catch {
      if (mountedRef.current) setError('Failed to save. Please try again.');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleClear = async () => {
    setError(null);
    setSaved(false);
    try {
      setSaving(true);
      await defaultCenterAPI.set(null, null, null);
      if (!mountedRef.current) return;
      setCurrent(null);
      setAuthDefaultCenter(null);
      setLatInput('');
      setLngInput('');
      setZoomInput('10');
      setSaved(true);
    } catch {
      if (mountedRef.current) setError('Failed to clear. Please try again.');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const pickerCenter: LatLng =
    pickedOnMap ??
    (current ? [current.lat, current.lng] : null) ??
    (parseFloat(latInput) && parseFloat(lngInput)
      ? [parseFloat(latInput), parseFloat(lngInput)]
      : [20, 0]);

  const containerStyle: React.CSSProperties = inline
    ? {}
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 'max(20px, 5vh)',
        zIndex: 5000,
      };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '3vmin', fontSize: '2vmin', color: '#666' }}>Loading…</div>
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    padding: '0.8vmin 1vmin',
    borderRadius: '0.4vmin',
    border: '1px solid #ccc',
    fontSize: '1.8vmin',
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '1.5vmin',
    color: '#555',
    marginBottom: '0.4vmin',
    display: 'block',
  };

  return (
    <div style={containerStyle} onClick={inline ? undefined : onClose}>
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          ...(inline ? {} : { maxWidth: '480px', width: '90%', maxHeight: 'min(90vh, 85dvh)' }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: inline ? 'none' : '0 8px 16px rgba(0,0,0,0.3)',
        }}
        onClick={inline ? undefined : (e) => e.stopPropagation()}
      >
        <div
          style={{
            ...(inline ? {} : { overflowY: 'scroll', flex: 1 }),
            padding: '2.5vmin',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2vmin',
              borderBottom: '2px solid #ddd',
              paddingBottom: '1.5vmin',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '2.5vmin', color: '#333' }}>Default Map Center</h2>
            {!inline && (
              <Button
                text="✕"
                onClick={onClose}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                  borderRadius: '0.3vmin',
                }}
              />
            )}
          </div>

          <p style={{ fontSize: '1.6vmin', color: '#666', margin: '0 0 2vmin' }}>
            The map will start centered here when no device positions are available.
          </p>

          {/* Current value */}
          <div
            style={{
              background: '#f9f9f9',
              border: '1px solid #eee',
              borderRadius: '0.5vmin',
              padding: '1.2vmin 1.5vmin',
              marginBottom: '2vmin',
              fontSize: '1.7vmin',
            }}
          >
            <span style={{ color: '#777', marginRight: '1vmin' }}>Current:</span>
            {current ? (
              <strong>
                {current.lat}, {current.lng}
                {current.zoom != null && (
                  <span style={{ marginLeft: '1vmin', color: '#555' }}> zoom {current.zoom}</span>
                )}
              </strong>
            ) : (
              <span style={{ color: '#aaa' }}>No default center saved.</span>
            )}
          </div>

          {/* Coordinate and zoom inputs */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 0.6fr',
              gap: '1.5vmin',
              marginBottom: '2vmin',
            }}
          >
            <div>
              <label style={labelStyle}>Latitude</label>
              <input
                type="number"
                step="any"
                min={-90}
                max={90}
                value={latInput}
                onChange={(e) => {
                  setLatInput(e.target.value);
                  setSaved(false);
                }}
                placeholder="e.g. 55.7161"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Longitude</label>
              <input
                type="number"
                step="any"
                min={-180}
                max={180}
                value={lngInput}
                onChange={(e) => {
                  setLngInput(e.target.value);
                  setSaved(false);
                }}
                placeholder="e.g. 13.2249"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Zoom (1–20)</label>
              <input
                type="number"
                step={1}
                min={1}
                max={20}
                value={zoomInput}
                onChange={(e) => {
                  setZoomInput(e.target.value);
                  setSaved(false);
                }}
                placeholder="10"
                style={inputStyle}
              />
            </div>
          </div>

          {error && (
            <p style={{ color: '#c00', fontSize: '1.6vmin', margin: '0 0 1.5vmin' }}>{error}</p>
          )}
          {saved && (
            <p style={{ color: '#080', fontSize: '1.6vmin', margin: '0 0 1.5vmin' }}>Saved.</p>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '1vmin', flexWrap: 'wrap' }}>
            <Button
              text="🗺️ Pick on map"
              onClick={() => setPickerOpen(true)}
              style={{ fontSize: '1.8vmin', padding: '0.8vmin 1.5vmin' }}
            />
            <Button
              text={saving ? 'Saving…' : 'Save'}
              onClick={handleSave}
              disabled={saving || !latInput || !lngInput}
              style={{ fontSize: '1.8vmin', padding: '0.8vmin 1.5vmin' }}
            />
            {current && (
              <Button
                text="Clear"
                onClick={handleClear}
                disabled={saving}
                style={{
                  fontSize: '1.8vmin',
                  padding: '0.8vmin 1.5vmin',
                  background: '#eee',
                  color: '#333',
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Map picker modal */}
      {pickerOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 6000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setPickerOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '1vmin',
              padding: '2vmin',
              width: 'min(90vw, 680px)',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5vmin',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eee',
                paddingBottom: '1vmin',
              }}
            >
              <p style={{ margin: 0, fontSize: '2vmin', fontWeight: 600 }}>
                Click on the map to set the default center
              </p>
              {pickedOnMap && (
                <span style={{ fontSize: '1.6vmin', color: '#555' }}>
                  {pickedOnMap[0].toFixed(6)}, {pickedOnMap[1].toFixed(6)}
                </span>
              )}
            </div>

            <MapContainer
              center={pickerCenter}
              zoom={10}
              style={{
                height: 'min(50vh, 420px)',
                width: '100%',
                borderRadius: '0.5vmin',
                cursor: 'crosshair',
              }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapClickHandler onPick={handlePickOnMap} picked={pickedOnMap} />
            </MapContainer>

            <div style={{ display: 'flex', gap: '1vmin', justifyContent: 'flex-end' }}>
              <Button
                text="Cancel"
                onClick={() => setPickerOpen(false)}
                style={{
                  fontSize: '1.8vmin',
                  padding: '0.8vmin 1.5vmin',
                  background: '#eee',
                  color: '#333',
                }}
              />
              <Button
                text="Use this location"
                onClick={handleUsePickedLocation}
                disabled={!pickedOnMap}
                style={{ fontSize: '1.8vmin', padding: '0.8vmin 1.5vmin' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
